import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquarePlus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Check,
  X,
  LogOut,
  Moon,
  Sun,
  PanelLeftClose,
  Ellipsis,
  Archive,
} from 'lucide-react';
import { useChatStore, groupChats } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { Chat } from '../types';

const SIDEBAR_WIDTH = 260;

const Sidebar: React.FC = () => {
  const chats = useChatStore((s) => s.chats);
  const activeChatId = useChatStore((s) => s.activeChatId);
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const createNewChat = useChatStore((s) => s.createNewChat);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const deleteChat = useChatStore((s) => s.deleteChat);
  const renameChat = useChatStore((s) => s.renameChat);
  const clearAllChats = useChatStore((s) => s.clearAllChats);
  const toggleSidebar = useChatStore((s) => s.toggleSidebar);

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const chatGroups = useMemo(() => groupChats(chats), [chats]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery) return chatGroups;
    return chatGroups
      .map((group) => ({
        ...group,
        chats: group.chats.filter((chat) =>
          chat.title.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      }))
      .filter((group) => group.chats.length > 0);
  }, [chatGroups, searchQuery]);

  // ─── Mobile: overlay + slide ───
  if (isMobile) {
    return (
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 z-40"
              onClick={toggleSidebar}
            />
            <motion.aside
              initial={{ x: -SIDEBAR_WIDTH }}
              animate={{ x: 0 }}
              exit={{ x: -SIDEBAR_WIDTH }}
              transition={{ type: 'spring', damping: 30, stiffness: 350 }}
              className={`fixed left-0 top-0 bottom-0 z-50 flex flex-col h-full ${
                theme === 'dark' ? 'bg-gpt-dark-sidebar' : 'bg-gpt-light-sidebar'
              }`}
              style={{ width: SIDEBAR_WIDTH }}
            >
              <SidebarContent
                theme={theme}
                sidebarOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                createNewChat={createNewChat}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filteredGroups={filteredGroups}
                activeChatId={activeChatId}
                setActiveChat={setActiveChat}
                renameChat={renameChat}
                deleteChat={deleteChat}
                user={user}
                showUserMenu={showUserMenu}
                setShowUserMenu={setShowUserMenu}
                userMenuRef={userMenuRef}
                toggleTheme={toggleTheme}
                clearAllChats={clearAllChats}
                logout={logout}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  // ─── Desktop: animated width, always in DOM ───
  return (
    <motion.aside
      initial={false}
      animate={{
        width: sidebarOpen ? SIDEBAR_WIDTH : 0,
      }}
      transition={{
        type: 'spring',
        damping: 26,
        stiffness: 260,
        mass: 0.8,
      }}
      className={`relative flex-shrink-0 h-full overflow-hidden ${
        theme === 'dark' ? 'bg-gpt-dark-sidebar' : 'bg-gpt-light-sidebar'
      }`}
    >
      {/* Inner wrapper keeps content at full width so it doesn't squish */}
      <motion.div
        initial={false}
        animate={{
          opacity: sidebarOpen ? 1 : 0,
          x: sidebarOpen ? 0 : -20,
        }}
        transition={{
          opacity: { duration: sidebarOpen ? 0.25 : 0.1, delay: sidebarOpen ? 0.1 : 0 },
          x: { type: 'spring', damping: 26, stiffness: 260, mass: 0.8 },
        }}
        className="flex flex-col h-full"
        style={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH }}
      >
        <SidebarContent
          theme={theme}
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          createNewChat={createNewChat}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredGroups={filteredGroups}
          activeChatId={activeChatId}
          setActiveChat={setActiveChat}
          renameChat={renameChat}
          deleteChat={deleteChat}
          user={user}
          showUserMenu={showUserMenu}
          setShowUserMenu={setShowUserMenu}
          userMenuRef={userMenuRef}
          toggleTheme={toggleTheme}
          clearAllChats={clearAllChats}
          logout={logout}
        />
      </motion.div>
    </motion.aside>
  );
};

// ─── Sidebar Content (shared between mobile & desktop) ───
interface SidebarContentProps {
  theme: string;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  createNewChat: () => string;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  filteredGroups: { label: string; chats: Chat[] }[];
  activeChatId: string | null;
  setActiveChat: (id: string) => void;
  renameChat: (id: string, title: string) => void;
  deleteChat: (id: string) => void;
  user: any;
  showUserMenu: boolean;
  setShowUserMenu: (v: boolean) => void;
  userMenuRef: React.RefObject<HTMLDivElement | null>;
  toggleTheme: () => void;
  clearAllChats: () => void;
  logout: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  theme,
  toggleSidebar,
  createNewChat,
  searchQuery,
  setSearchQuery,
  filteredGroups,
  activeChatId,
  setActiveChat,
  renameChat,
  deleteChat,
  user,
  showUserMenu,
  setShowUserMenu,
  userMenuRef,
  toggleTheme,
  clearAllChats,
  logout,
}) => {
  return (
    <>
      {/* Top Section */}
      <div className="flex items-center justify-between p-2 h-[60px]">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleSidebar}
          className={`p-2 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
              : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
          }`}
          title="Close sidebar"
        >
          <PanelLeftClose size={20} />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => createNewChat()}
          className={`p-2 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
              : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
          }`}
          title="New chat"
        >
          <MessageSquarePlus size={20} />
        </motion.button>
      </div>

      {/* Search */}
      <div className="px-2 pb-2">
        <div className="relative">
          <Search
            size={16}
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${
              theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
            }`}
          />
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border outline-none transition-colors ${
              theme === 'dark'
                ? 'bg-gpt-dark-main border-gpt-dark-border text-gpt-dark-text placeholder:text-gpt-dark-text-secondary focus:border-gpt-dark-text-secondary'
                : 'bg-white border-gpt-light-border text-gpt-light-text placeholder:text-gpt-light-text-secondary focus:border-gpt-light-text-secondary'
            }`}
          />
        </div>
      </div>

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto px-2 space-y-4">
        {filteredGroups.length === 0 && (
          <div
            className={`text-center text-sm py-8 ${
              theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
            }`}
          >
            {searchQuery ? 'No chats found' : 'No chat history'}
          </div>
        )}
        {filteredGroups.map((group) => (
          <div key={group.label}>
            <h3
              className={`text-xs font-semibold px-2 py-1 ${
                theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
              }`}
            >
              {group.label}
            </h3>
            <div className="space-y-0.5">
              {group.chats.map((chat) => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  isActive={chat.id === activeChatId}
                  theme={theme}
                  onSelect={() => setActiveChat(chat.id)}
                  onRename={(title) => renameChat(chat.id, title)}
                  onDelete={() => deleteChat(chat.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* User Section */}
      <div
        className={`p-2 border-t ${
          theme === 'dark' ? 'border-gpt-dark-border/30' : 'border-gpt-light-border'
        }`}
      >
        <div className="relative" ref={userMenuRef}>
          <motion.button
            whileHover={{
              backgroundColor: theme === 'dark' ? '#2f2f2f' : '#ececec',
            }}
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg transition-colors text-left"
          >
            <div className="w-8 h-8 rounded-full bg-gpt-green flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user?.displayName?.charAt(0)?.toUpperCase() ||
                user?.email?.charAt(0)?.toUpperCase() ||
                'U'}
            </div>
            <span
              className={`text-sm truncate flex-1 ${
                theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
              }`}
            >
              {user?.displayName || user?.email || 'User'}
            </span>
            <Ellipsis
              size={16}
              className={
                theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
              }
            />
          </motion.button>

          <AnimatePresence>
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className={`absolute bottom-full left-0 right-0 mb-1 rounded-xl shadow-lg border overflow-hidden ${
                  theme === 'dark'
                    ? 'bg-gpt-dark-input border-gpt-dark-border'
                    : 'bg-white border-gpt-light-border'
                }`}
              >
                <button
                  onClick={() => {
                    toggleTheme();
                    setShowUserMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    theme === 'dark'
                      ? 'text-gpt-dark-text hover:bg-gpt-dark-hover'
                      : 'text-gpt-light-text hover:bg-gpt-light-hover'
                  }`}
                >
                  {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>

                <button
                  onClick={() => {
                    clearAllChats();
                    setShowUserMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    theme === 'dark'
                      ? 'text-gpt-dark-text hover:bg-gpt-dark-hover'
                      : 'text-gpt-light-text hover:bg-gpt-light-hover'
                  }`}
                >
                  <Archive size={16} />
                  Clear all chats
                </button>

                <div
                  className={`border-t ${
                    theme === 'dark' ? 'border-gpt-dark-border' : 'border-gpt-light-border'
                  }`}
                />

                <button
                  onClick={() => {
                    logout();
                    setShowUserMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    theme === 'dark'
                      ? 'text-gpt-dark-text hover:bg-gpt-dark-hover'
                      : 'text-gpt-light-text hover:bg-gpt-light-hover'
                  }`}
                >
                  <LogOut size={16} />
                  Log out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

// ─── Chat Item ───
interface ChatItemProps {
  chat: Chat;
  isActive: boolean;
  theme: string;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}

const ChatItem: React.FC<ChatItemProps> = React.memo(
  ({ chat, isActive, theme, onSelect, onRename, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(chat.title);
    const [showActions, setShowActions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
          setShowActions(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
      if (!isEditing) setEditTitle(chat.title);
    }, [chat.title, isEditing]);

    const handleSaveEdit = () => {
      if (editTitle.trim()) onRename(editTitle.trim());
      setIsEditing(false);
    };

    const handleCancelEdit = () => {
      setEditTitle(chat.title);
      setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSaveEdit();
      if (e.key === 'Escape') handleCancelEdit();
    };

    return (
      <div
        className={`group relative flex items-center rounded-lg cursor-pointer transition-colors ${
          isActive
            ? theme === 'dark'
              ? 'bg-gpt-dark-hover'
              : 'bg-gpt-light-hover'
            : theme === 'dark'
            ? 'hover:bg-gpt-dark-hover/50'
            : 'hover:bg-gpt-light-hover/50'
        }`}
        onClick={() => !isEditing && onSelect()}
      >
        {isEditing ? (
          <div className="flex items-center gap-1 w-full px-2 py-2">
            <input
              ref={inputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`flex-1 text-sm bg-transparent outline-none border rounded px-1 py-0.5 ${
                theme === 'dark'
                  ? 'border-gpt-dark-border text-gpt-dark-text'
                  : 'border-gpt-light-border text-gpt-light-text'
              }`}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              className="p-1 rounded hover:bg-green-500/20 text-green-500"
            >
              <Check size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              className={`p-1 rounded ${
                theme === 'dark'
                  ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
                  : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
              }`}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 px-3 py-2 min-w-0">
              <span
                className={`text-sm truncate block ${
                  theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
                }`}
              >
                {chat.title}
              </span>
            </div>

            <div
              className={`absolute right-0 top-0 bottom-0 w-20 pointer-events-none rounded-r-lg ${
                isActive || showActions ? '' : 'opacity-0 group-hover:opacity-100'
              } ${
                isActive
                  ? theme === 'dark'
                    ? 'bg-gradient-to-l from-gpt-dark-hover'
                    : 'bg-gradient-to-l from-gpt-light-hover'
                  : theme === 'dark'
                  ? 'bg-gradient-to-l from-gpt-dark-hover/50'
                  : 'bg-gradient-to-l from-gpt-light-hover/50'
              }`}
            />

            <div
              ref={actionsRef}
              className={`absolute right-1 flex items-center gap-0.5 transition-opacity ${
                isActive || showActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(!showActions);
                }}
                className={`p-1 rounded transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gpt-dark-border/50 text-gpt-dark-text-secondary'
                    : 'hover:bg-gpt-light-border text-gpt-light-text-secondary'
                }`}
              >
                <MoreHorizontal size={16} />
              </button>

              <AnimatePresence>
                {showActions && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                    transition={{ duration: 0.1 }}
                    className={`absolute right-0 top-full mt-1 z-50 rounded-xl shadow-lg border overflow-hidden min-w-[150px] ${
                      theme === 'dark'
                        ? 'bg-gpt-dark-input border-gpt-dark-border'
                        : 'bg-white border-gpt-light-border'
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActions(false);
                        setIsEditing(true);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition-colors ${
                        theme === 'dark'
                          ? 'text-gpt-dark-text hover:bg-gpt-dark-hover'
                          : 'text-gpt-light-text hover:bg-gpt-light-hover'
                      }`}
                    >
                      <Pencil size={14} />
                      Rename
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActions(false);
                        onDelete();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    );
  }
);

ChatItem.displayName = 'ChatItem';

export default Sidebar;